import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const AnimatedCounter = ({ 
  value, 
  prefix = '', 
  suffix = '', 
  decimals = 0,
  duration = 1.5,
  className = '',
  delay = 0
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const numericValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;

  useEffect(() => {
    if (!isInView) return;
    
    const startTime = Date.now() + delay * 1000;
    const endTime = startTime + duration * 1000;
    
    const updateValue = () => {
      const now = Date.now();
      
      if (now < startTime) {
        requestAnimationFrame(updateValue);
        return;
      }
      
      if (now >= endTime) {
        setDisplayValue(numericValue);
        return;
      }
      
      const progress = (now - startTime) / (duration * 1000);
      const easeProgress = 1 - Math.pow(1 - progress, 3); // Ease out cubic
      const currentValue = numericValue * easeProgress;
      setDisplayValue(currentValue);
      requestAnimationFrame(updateValue);
    };
    
    requestAnimationFrame(updateValue);
  }, [isInView, numericValue, duration, delay]);

  const formatValue = (val) => {
    if (decimals > 0) {
      return val.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return Math.floor(val).toLocaleString();
  };

  return (
    <motion.span
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 10 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.3, delay }}
    >
      {prefix}{formatValue(displayValue)}{suffix}
    </motion.span>
  );
};

export default AnimatedCounter;
